import React from 'react';
import { useState, useEffect } from 'react';
import {
  Mail, Phone, Linkedin, MessageSquare, Calendar, Search,
  FileSpreadsheet, Send,
  Target, Zap, CheckCircle, DollarSign, Clock, Users, GraduationCap, Settings, Eye,
  User, MessageCircle, UserCheck, CalendarCheck
} from 'lucide-react';

const MazeVsDirectSection: React.FC = () => {
  const [currentAgentIndex, setCurrentAgentIndex] = React.useState(0);
  const [leadState, setLeadState] = React.useState('raw');
  const [isDesktop, setIsDesktop] = useState(false);

  // Track desktop screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const aiAgents = [
    {
      id: 1,
      name: 'Lead Gen Agent',
      icon: Search,
      action: 'Lead Found',
      leadTransform: 'found',
      color: '#2F7BFF'
    },
    {
      id: 2,
      name: 'Outreach Agent',
      icon: MessageCircle,
      action: 'Outreaching',
      leadTransform: 'outreach',
      color: '#12E6A5'
    },
    {
      id: 3,
      name: 'Meeting Done',
      icon: Calendar,
      action: 'Following Up',
      leadTransform: 'booked',
      color: '#FFD700'
    },
    {
      id: 4,
      name: 'Deal Closed',
      icon: DollarSign,
      action: '',
      leadTransform: 'closed',
      color: '#00FF00'
    }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAgentIndex((prev) => {
        const nextIndex = (prev + 1) % aiAgents.length;
        setLeadState(aiAgents[nextIndex].leadTransform);
        return nextIndex;
      });
    }, 2500);

    return () => clearInterval(interval);
  }, [aiAgents.length]);

  const currentAgent = aiAgents[currentAgentIndex];

  // Desktop positions: exactly between stages and on final stage
  const desktopLeadUnitPositions = [23, 51, 77, 92]; // Custom precise positions
  const defaultLeadUnitPositions = [16.67, 50, 83.33, 95]; // Original positions for mobile/tablet

  const solutionPoints = [
    { text: '60% cheaper', icon: DollarSign },
    { text: 'Works 24/7', icon: Clock },
    { text: 'No hiring', icon: Users },
    { text: 'No training', icon: GraduationCap },
    { text: 'No managing', icon: Settings },
    { text: 'Full visibility', icon: Eye }
  ];

  return (
    <section className="relative py-8 sm:py-12 lg:py-8 px-4 sm:px-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Headline */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-text-primary font-black text-2xl sm:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4 tracking-tight">
            Sell Smarter, Not Harder
          </h2>
          <div className="w-16 sm:w-20 lg:w-24 h-1 bg-gradient-brand mx-auto"></div>
        </div>

        {/* Cards Grid */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* BEFORE CARD - THE MAZE */}
          <div className="relative bg-bg-surface border border-card-border rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl overflow-hidden min-h-[280px] sm:min-h-[450px] lg:h-[500px]">
            {/* Maze Atmosphere Overlay */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-br from-bg-surface via-bg-deep/80 to-bg-surface opacity-60"></div>
              <div className="absolute inset-0 maze-fog"></div>
            </div>

            {/* Title */}
            <div className="relative z-10 mb-4 sm:mb-6 lg:mb-8">
              <h3 className="text-text-primary font-bold text-xl sm:text-2xl lg:text-3xl mb-2 sm:mb-3">
                Traditional Sales
              </h3>
              <div className="w-12 sm:w-14 lg:w-16 h-0.5 bg-gradient-to-r from-red-400 to-orange-400"></div>
            </div>

            {/* Tools Illustration (Upper Section) */}
            <div className="relative h-32 sm:h-36 mb-4 sm:mb-6">
              {/* Scattered Tools */}
              <div className="absolute inset-0">
                {/* Mail — red | top band, far left */}
                <div className="absolute top-2 left-4 w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '0s', backgroundColor: '#EA4335' }}>
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                {/* Mailchimp — top band, left-center | sm+ only */}
                <div className="hidden sm:block absolute top-6 left-28 w-7 h-7 sm:w-8 sm:h-8 rounded-xl overflow-hidden tool-dance shadow-lg border border-white/10" style={{ animationDelay: '2.5s' }}>
                  <img src="/landing-page/mailchimp logo.jpg" alt="Mailchimp" className="w-full h-full object-cover" />
                </div>
                {/* Excel — top band, right-of-center | sm+ only */}
                <div className="hidden sm:flex absolute top-2 left-40 w-7 h-7 sm:w-8 sm:h-8 rounded-xl items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '4.5s', backgroundColor: '#217346' }}>
                  <span className="text-white font-bold text-sm sm:text-base leading-none">X</span>
                </div>
                {/* Apollo — top band, right side */}
                <div className="absolute top-1 right-32 w-7 h-7 sm:w-8 sm:h-8 rounded-xl overflow-hidden tool-dance shadow-lg border border-white/10" style={{ animationDelay: '3.5s' }}>
                  <img src="/landing-page/Apollo logo.png" alt="Apollo" className="w-full h-full object-cover" />
                </div>
                {/* Clay — top band, far right */}
                <div className="absolute top-2 right-4 w-7 h-7 sm:w-8 sm:h-8 rounded-xl overflow-hidden tool-dance shadow-lg border border-white/10" style={{ animationDelay: '5s' }}>
                  <img src="/landing-page/clay logo.png" alt="Clay" className="w-full h-full object-cover" />
                </div>
                {/* WhatsApp — middle band, left */}
                <div className="absolute top-14 left-2 w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '1s', backgroundColor: '#25D366' }}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                </div>
                {/* Send — middle band, left-center */}
                <div className="absolute top-[72px] left-24 w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '4s', backgroundColor: '#2F7BFF' }}>
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                {/* HubSpot — middle band, right side */}
                <div className="absolute top-14 right-24 w-7 h-7 sm:w-8 sm:h-8 rounded-xl overflow-hidden tool-dance shadow-lg border border-white/10" style={{ animationDelay: '3s' }}>
                  <img src="/landing-page/hubspot logo.jpg" alt="HubSpot" className="w-full h-full object-cover" />
                </div>
                {/* LinkedIn — middle band, far right */}
                <div className="absolute top-12 right-6 w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '1.5s', backgroundColor: '#0A66C2' }}>
                  <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                {/* Calendar — bottom band, left */}
                <div className="absolute top-24 left-8 w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '2s', backgroundColor: '#4285F4' }}>
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                {/* Lusha — bottom band, center-left */}
                <div className="absolute top-[88px] right-36 w-7 h-7 sm:w-8 sm:h-8 rounded-xl overflow-hidden tool-dance shadow-lg border border-white/10" style={{ animationDelay: '5.5s' }}>
                  <img src="/landing-page/lusha logo.jpg" alt="Lusha" className="w-full h-full object-cover" />
                </div>
                {/* Phone — bottom band, far right */}
                <div className="absolute top-[88px] right-4 w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center tool-dance shadow-lg" style={{ animationDelay: '0.5s', backgroundColor: '#0F9D58' }}>
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              </div>

              {/* Failed Connection Lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 120">
                <defs>
                  <linearGradient id="failedConnectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#f97316" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
                
                {/* Multiple failed connection attempts */}
                <g>
                  <path
                    d="M32,16 Q60,10 85,30"
                    stroke="url(#failedConnectionGradient)"
                    strokeWidth="2"
                    strokeDasharray="3,6"
                    fill="none"
                    className="failed-connection-line"
                    style={{ animationDelay: '1s' }}
                  />
                  <circle cx="85" cy="30" r="2" fill="#ef4444" className="failed-connection-target" style={{ animationDelay: '2.5s' }} />
                </g>
                
                <g>
                  <path
                    d="M320,40 Q280,20 255,45"
                    stroke="url(#failedConnectionGradient)"
                    strokeWidth="2"
                    strokeDasharray="3,6"
                    fill="none"
                    className="failed-connection-line"
                    style={{ animationDelay: '2s' }}
                  />
                  <circle cx="255" cy="45" r="2" fill="#ef4444" className="failed-connection-target" style={{ animationDelay: '3.5s' }} />
                </g>
                
                <g>
                  <path
                    d="M64,64 Q120,80 175,58"
                    stroke="url(#failedConnectionGradient)"
                    strokeWidth="2"
                    strokeDasharray="3,6"
                    fill="none"
                    className="failed-connection-line"
                    style={{ animationDelay: '3s' }}
                  />
                  <circle cx="175" cy="58" r="2" fill="#ef4444" className="failed-connection-target" style={{ animationDelay: '4.5s' }} />
                </g>
                
                <g>
                  <path
                    d="M280,72 Q320,60 358,78"
                    stroke="url(#failedConnectionGradient)"
                    strokeWidth="2"
                    strokeDasharray="3,6"
                    fill="none"
                    className="failed-connection-line"
                    style={{ animationDelay: '4s' }}
                  />
                  <circle cx="358" cy="78" r="2" fill="#ef4444" className="failed-connection-target" style={{ animationDelay: '5.5s' }} />
                </g>
                
                <g>
                  <path
                    d="M112,24 Q160,40 205,22"
                    stroke="url(#failedConnectionGradient)"
                    strokeWidth="2"
                    strokeDasharray="3,6"
                    fill="none"
                    className="failed-connection-line"
                    style={{ animationDelay: '5s' }}
                  />
                  <circle cx="205" cy="22" r="2" fill="#ef4444" className="failed-connection-target" style={{ animationDelay: '6.5s' }} />
                </g>
              </svg>

            </div>

            {/* Problem Boxes Grid */}
            <div className="relative z-10">
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {[
                  { text: '60% more expensive', icon: '💸' },
                  { text: 'Works 8 hours/day', icon: '⏰' },
                  { text: 'Constant hiring', icon: '👥' },
                  { text: 'Long training', icon: '📚' },
                  { text: 'Daily management', icon: '📊' },
                  { text: 'Limited visibility', icon: '👁️' }
                ].map((problem, index) => (
                  <div
                    key={index}
                    className="problem-box h-12 sm:h-14"
                    style={{ animationDelay: `${index * 0.3}s` }}
                  >
                    {/* Tangled Connection Lines SVG */}
                    <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-50 sm:opacity-100" viewBox="0 0 100 60">
                      <path
                        d="M10,10 Q30,5 50,15 T90,10 Q70,25 40,30 T10,50"
                        stroke="rgba(239, 68, 68, 0.3)"
                        strokeWidth="1"
                        strokeDasharray="2,4"
                        fill="none"
                        className="tangle-line"
                        style={{ animationDelay: `${index * 0.5}s` }}
                      />
                    </svg>
                    
                    {/* Problem Icon */}
                    <div className="problem-icon">
                      {problem.icon}
                    </div>
                    
                    {/* Problem Text */}
                    <div className="problem-text">
                      {problem.text}
                    </div>
                    
                    {/* Glitch Overlay */}
                    <div className="glitch-overlay"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* AFTER CARD - THE DIRECT PATH */}
          <div className="relative bg-bg-surface border border-card-border rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl overflow-hidden min-h-[280px] sm:min-h-[450px] lg:h-[500px]">
            {/* Direct Path Atmosphere */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-gradient-to-br from-bg-surface via-brand-blue/5 to-bg-surface"></div>
            </div>

            {/* Title */}
            <div className="relative z-10 mb-4 sm:mb-6 lg:mb-8">
              <h3 className="text-text-primary font-bold text-xl sm:text-2xl lg:text-3xl mb-2 sm:mb-3">
                AI Sales Team
              </h3>
              <div className="w-12 sm:w-14 lg:w-16 h-0.5 bg-gradient-brand"></div>
            </div>

            {/* Direct Path Visual */}
            <div className="relative h-20 sm:h-24 lg:h-28 mb-4 sm:mb-6 flex items-center justify-center overflow-hidden">
              {/* Mobile-only Fixed Text Above Flow */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-20 text-center sm:hidden">
                <span className="text-text-primary text-xs font-semibold">
                  {['Lead Found', 'Outreaching', 'Following Up', 'Deal Closed'][currentAgentIndex]}
                </span>
              </div>

              {/* AI Sales Processing Line - Compact Version */}
              <div className="relative w-full max-w-xs sm:max-w-sm lg:max-w-md">
                {/* Processing Track */}
                <div className="relative h-8 sm:h-10 lg:h-12 bg-gradient-to-r from-card-border/30 via-card-border/50 to-card-border/30 rounded-full">
                  {/* Processing Stations */}
                  <div className="absolute inset-0 flex items-center justify-between px-2 sm:px-3 lg:px-4">
                    {aiAgents.map((agent, index) => {
                      const IconComponent = agent.icon;
                      const isActive = currentAgentIndex === index;
                      const isCompleted = currentAgentIndex > index;
                      
                      return (
                        <div key={agent.id} className="relative">
                          {/* Processing Station */}
                          <div 
                            className={`
                              w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full border-2 flex items-center justify-center transition-all duration-500
                              ${isActive 
                                ? 'bg-chip-bg border-brand-blue shadow-glow-blue animate-pulse' 
                                : isCompleted
                                  ? 'bg-chip-bg border-brand-green-teal shadow-chip-glow'
                                  : 'bg-bg-surface border-card-border'
                              }
                            `}
                          >
                            <IconComponent 
                              className={`
                                w-2 h-2 sm:w-3 sm:h-3 lg:w-4 lg:h-4 transition-colors duration-500
                                ${isActive 
                                  ? 'text-brand-blue' 
                                  : isCompleted
                                    ? 'text-brand-green-teal'
                                    : 'text-text-secondary/50'
                                }
                              `}
                            />
                          </div>
                          
                          {/* Station Processing Effect */}
                          {isActive && (
                            <div 
                              key={`effect-${agent.id}`}
                              className={`
                                absolute inset-0 rounded-full pointer-events-none
                                ${leadState === 'qualified' ? 'station-scan-effect' : ''}
                                ${leadState === 'engaged' ? 'station-message-effect' : ''}
                                ${leadState === 'nurtured' ? 'station-nurture-effect' : ''}
                                ${leadState === 'booked' ? 'station-book-effect' : ''}
                              `}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Lead Unit */}
                  <div 
                    className={`
                      absolute top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-lg
                      flex items-center justify-center transition-all duration-700 ease-out
                      lead-unit-flow -translate-x-1/2
                      ${leadState === 'raw' ? 'bg-gray-600/40 border border-gray-500' : ''}
                      ${leadState === 'found' ? 'bg-blue-500/40 border border-blue-400' : ''}
                      ${leadState === 'outreach' ? 'bg-green-500/40 border border-green-400' : ''}
                      ${leadState === 'booked' ? 'bg-red-500/40 border border-red-400' : ''}
                      ${leadState === 'followup' ? 'bg-yellow-500/40 border border-yellow-400' : ''}
                      ${leadState === 'closed' ? 'bg-green-500/50 border border-green-400 shadow-glow-green' : ''}
                    `}
                    style={{
                      left: `${(isDesktop ? desktopLeadUnitPositions : defaultLeadUnitPositions)[currentAgentIndex] || (isDesktop ? 25 : 16.67)}%`
                    }}
                  >
                    {/* Desktop-only Text Label Above Moving Icon */}
                    <div className="absolute -top-6 sm:-top-8 lg:-top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap z-20 hidden sm:block">
                      <div className="text-center">
                        <span className="text-text-primary text-xs sm:text-xs lg:text-xs font-semibold">
                          {['Lead Found', 'Outreaching', 'Following Up', 'Deal Closed'][currentAgentIndex]}
                        </span>
                      </div>
                    </div>

                    {/* Lead Unit Content */}
                    {currentAgentIndex === 3 && leadState === 'closed' ? (
                      <DollarSign className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-green-300 font-bold" />
                    ) : (
                      <User className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 text-gray-400" />
                    )}
                    
                    {/* Stage-specific overlays for visual feedback */}
                    {currentAgentIndex === 0 && leadState === 'found' && (
                      <Search className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 text-blue-400" />
                    )}
                    {currentAgentIndex === 1 && leadState === 'outreach' && (
                      <MessageCircle className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 text-green-400" />
                    )}
                    {currentAgentIndex === 2 && leadState === 'booked' && (
                      <CalendarCheck className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3 text-yellow-400" />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Solution Boxes Grid */}
            <div className="relative z-10">
              {/* Desktop (only) — fixed-size tiles */}
              <ul className="hidden lg:grid lg:grid-cols-2 lg:gap-4 lg:[grid-auto-rows:56px]">
                {solutionPoints.map((solution, index) => {
                  const IconComponent = solution.icon;
                  return (
                    <li key={index}>
                      <div className="h-full w-full box-border rounded-xl border border-chip-border/70 px-4 flex items-center">
                        <div className="text-brand-green-teal flex-shrink-0 mr-3">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <span className="text-chip-text font-medium lg:text-base truncate">
                          {solution.text}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* Non-desktop stays as-is */}
              <div className="lg:hidden grid grid-cols-2 gap-2 sm:gap-3">
                {solutionPoints.map((solution, index) => {
                  const IconComponent = solution.icon;
                  return (
                    <div
                      key={index}
                      className="solution-box h-10 sm:h-14"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="solution-icon">
                        <IconComponent className="w-3 h-3 sm:w-4 sm:h-4" />
                      </div>
                      <div className="solution-text">
                        {solution.text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MazeVsDirectSection;